import { useDialog, type FileDetails } from "@faro-lotv/flat-ui";
import { compareDateTimes, type GUID } from "@faro-lotv/foundation";
import { useCallback, useEffect, useMemo, useState } from "react";

export type AnnotationAttachmentsReturn = {
  /** List of the file attachments of the annotation */
  attachments: FileDetails[];

  /** Function to add a new attachment to the list. It will allow the deletion if allowDeletion is true */
  addNewAttachment(attachment: FileDetails): void;
};

export type AnnotationFormVariant = "Create" | "Edit";

export type FileAttachment = {
  /** The unique id of the attachment */
  id: GUID;
  /** The name of the attachment */
  name: string;
  /** The file size in kilobytes */
  fileSize?: number | null;
  /** A string representing the creation date of the attachment */
  createdAt: string;
  /** The uri pointing to the source of the attachment */
  uri: string;
  /** The uir pointing to the source of the thumbnail */
  thumbnailUri?: string | null;
};

/**
 * @param fileAttachments IElements list of the annotation's attachments
 * @param formVariant type of the annotation's form
 * @param onDeletion Callback executed when the user click the deletion button
 * @param onConfirmDeletion Callback executed when the user confirms the deletion of an attachment
 * @param onCancelDeletion Callback executed when the user cancels the deletion of an attachment
 * @param initialAttachment Optional client-side generated attachment (e.g. a screenshot) to pre-seed the list with on mount
 * @returns the list of attachments and a function to add a new one to the list
 */
export function useAnnotationAttachmentsFUI(
  fileAttachments: FileAttachment[],
  formVariant: AnnotationFormVariant,
  onDeletion: (extension: string, isNewAttachment: boolean) => void,
  onConfirmDeletion: () => void,
  onCancelDeletion: () => void,
  initialAttachment?: FileDetails,
): AnnotationAttachmentsReturn {
  // Mirrors the server-persisted attachments from `fileAttachments`, converted to FileDetails.
  // Reset whenever `fileAttachments` changes.
  const [existingAttachments, setExistingAttachments] = useState<FileDetails[]>(
    [],
  );
  // Holds all local, not-yet-uploaded files: programmatically pre-seeded ones (e.g. a screenshot)
  // and any files the user adds manually during the session.
  const [pendingAttachments, setPendingAttachments] = useState<FileDetails[]>(
    () => (initialAttachment ? [initialAttachment] : []),
  );

  const { createDialog } = useDialog();

  /**
   * This function will be called when the delete button of an attachment will be clicked.
   * It removes a specific attachment from the local list.
   */
  const removeAttachment = useCallback(
    (id: string, extension: string, isNewAttachment: boolean) => {
      onDeletion(extension, isNewAttachment);
      createDialog({
        title: "Remove Attachment?",
        content: `The element will be permanently deleted${formVariant === "Edit" ? " from your project once the annotation is saved" : ""}. This operation cannot be undone.`,
        variant: "danger",
        dark: true,
        onConfirm: () => {
          onConfirmDeletion();
          setExistingAttachments((prev) => prev.filter((a) => a.id !== id));
          setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
          return true;
        },
        onCancel: onCancelDeletion,
      });
    },
    [
      createDialog,
      formVariant,
      onCancelDeletion,
      onConfirmDeletion,
      onDeletion,
    ],
  );

  const createFileDetails = useCallback(
    (attachmentsElements: FileAttachment[]): FileDetails[] =>
      attachmentsElements.map((el) => ({
        id: el.id,
        fileName: el.name,
        fileSize: el.fileSize,
        date: el.createdAt,
        urlOrFile: el.uri,
        thumbnailUrl: el.thumbnailUri,
        onDelete: removeAttachment,
      })),
    [removeAttachment],
  );

  useEffect(() => {
    setExistingAttachments(createFileDetails(fileAttachments));
  }, [fileAttachments, createFileDetails]);

  const addNewAttachment = useCallback(
    (attachment: FileDetails) =>
      setPendingAttachments((prev) => [...prev, attachment]),
    [],
  );

  const sortedAttachments = useMemo(
    () =>
      [
        ...pendingAttachments.map((pendingAttachment) => ({
          ...pendingAttachment,
          onDelete: removeAttachment,
        })),
        ...existingAttachments,
      ].sort((a: FileDetails, b: FileDetails) =>
        compareDateTimes(b.date, a.date),
      ),
    [pendingAttachments, existingAttachments, removeAttachment],
  );

  return {
    attachments: sortedAttachments,
    addNewAttachment,
  };
}
